/** Plain, bounded replay captions. No HTML/styles/remote resources are retained. */
export type CaptionCue = { start: number; end: number; text: string };
export type CaptionTrack = {
  language: string;
  label: string;
  enabled: boolean;
  cues: CaptionCue[];
};
export type ReplayCaptionData = { version: number; tracks: CaptionTrack[] };
export const CAPTION_FILE_BYTES = 256 * 1024;
export const MAX_CAPTION_CUES = 2000;
export function captionTime(value: string): number {
  const match = /^(?:(\d{2,3}):)?(\d{2}):(\d{2})[.,](\d{3})$/.exec(value);
  if (!match || Number(match[2]) > 59 || Number(match[3]) > 59)
    throw Error("Use timestamps such as 00:01:23.450");
  return (
    Number(match[1] ?? 0) * 3600 +
    Number(match[2]) * 60 +
    Number(match[3]) +
    Number(match[4]) / 1000
  );
}
export function plainCaption(text: string): string {
  return text
    .replace(/<[^>]*>/g, "")
    .replace(
      /&(amp|lt|gt|quot|apos|nbsp);/g,
      (_, entity: string) =>
        ({ amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " })[
          entity
        ]!,
    )
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "")
    .trim();
}
export function validateCues(
  cues: CaptionCue[],
  duration: number,
): CaptionCue[] {
  if (!cues.length || cues.length > MAX_CAPTION_CUES)
    throw Error(`Choose a file with 1–${MAX_CAPTION_CUES} captions`);
  if (!(duration > 0)) throw Error("Recording duration is unavailable");
  let previous = -1;
  for (const [index, cue] of cues.entries()) {
    if (
      !Number.isFinite(cue.start) ||
      !Number.isFinite(cue.end) ||
      cue.start < 0 ||
      cue.end <= cue.start ||
      cue.end > duration + 0.05
    )
      throw Error(
        `Caption ${index + 1} must start before it ends and fit inside this recording`,
      );
    if (cue.start < previous)
      throw Error(`Caption ${index + 1} is out of time order`);
    if (!cue.text.trim() || cue.text.length > 2000)
      throw Error(`Caption ${index + 1} must contain 1–2000 characters`);
    previous = cue.start;
  }
  if (new TextEncoder().encode(JSON.stringify(cues)).length > 512 * 1024)
    throw Error("This caption track is too large");
  return cues;
}
export function parseCaptions(source: string, duration: number): CaptionCue[] {
  if (new TextEncoder().encode(source).length > CAPTION_FILE_BYTES)
    throw Error("Choose a caption file smaller than 256 KB");
  const normalized = source
    .replace(/^\uFEFF/, "")
    .replace(/\r\n?/g, "\n")
    .trim();
  const blocks = normalized.split(/\n[ \t]*\n/);
  if (/^WEBVTT(?:\s|$)/.test(blocks[0] ?? "")) blocks.shift();
  const cues: CaptionCue[] = [];
  for (const block of blocks) {
    if (/^(?:NOTE(?:\s|$)|STYLE(?:\s|$)|REGION(?:\s|$))/.test(block)) continue;
    const lines = block.split("\n");
    let timing = lines.shift() ?? "";
    if (!timing.includes("-->")) timing = lines.shift() ?? "";
    const match = /^(\S+)\s+-->\s+(\S+)(?:\s+.*)?$/.exec(timing.trim());
    if (!match)
      throw Error(`Caption ${cues.length + 1} has an invalid time range`);
    cues.push({
      start: captionTime(match[1]),
      end: captionTime(match[2]),
      text: plainCaption(lines.join("\n")),
    });
  }
  return validateCues(cues, duration);
}
export function vttTime(seconds: number): string {
  const ms = Math.round(seconds * 1000);
  return `${String(Math.floor(ms / 3600000)).padStart(2, "0")}:${String(Math.floor(ms / 60000) % 60).padStart(2, "0")}:${String(Math.floor(ms / 1000) % 60).padStart(2, "0")}.${String(ms % 1000).padStart(3, "0")}`;
}
export const captionMarkup = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export function captionsVtt(cues: CaptionCue[]) {
  return (
    "WEBVTT\n\n" +
    cues
      .map(
        (c, i) =>
          `${i + 1}\n${vttTime(c.start)} --> ${vttTime(c.end)}\n${captionMarkup(c.text)}\n`,
      )
      .join("\n")
  );
}
